import React, { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useChangePassword, useMe } from '@app/api';
import { Input, PasswordInput } from '@app/form';
import { SubmitButton } from '@app/core';
import { useToast } from '@app/core';
import { I18n } from 'i18n';
import { Logger } from '../../../lib/logger';
import { ApiFormattedException } from '../../../store/modules/exceptions';
import { SettingContent } from './components/SettingContent';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h2,
    marginBottom: theme.marginBase * 6,
  },
}));

interface Props {}

const initialValues = {
  previousPassword: '',
  password: '',
  passwordRepeat: '',
};
type Values = typeof initialValues;

export const ChangePassword = (props: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ ...props, theme });

  const { t } = useTranslation();
  const { data: me } = useMe();

  const toast = useToast();
  const { mutateAsync: changePassword } = useChangePassword(me?.id);
  const accountId = me?.id;

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        previousPassword: Yup.string().min(6).required(),
        password: Yup.string().min(6).required(),
        passwordRepeat: Yup.string()
          .required()
          .min(6)
          .oneOf([Yup.ref('password'), ''], t('form.errors.passwordNotMatch')),
      }),
    [t],
  );

  const onSubmit = async (values: Values, helpers: FormikHelpers<Values>) => {
    try {
      if (!accountId) {
        Logger.assertionFailed('No account id on change password');
        throw Error('No account id on change password');
      }
      await changePassword(values);
      toast.success(t('settings.changePassword.success'));
    } catch (e) {
      if (
        e instanceof ApiFormattedException &&
        e.getCode() === 'INVALID_CREDENTIALS'
      ) {
        helpers.setFieldError(
          'previousPassword',
          t('settings.changePassword.previous.invalid'),
        );
        toast.error(t('toast.invalidForm'));
      } else {
        helpers.setStatus(e);
        toast.exception();
      }
    }
    helpers.setSubmitting(false);
  };

  return (
    <SettingContent>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <Form>
          <h1 className={classes.title}>
            <Trans i18nKey="settings.changePassword.title" />
          </h1>

          <Input
            name="previousPassword"
            label={t('settings.changePassword.previous.label')}
            placeholder={t('settings.changePassword.previous.placeholder')}
            type="password"
          />
          <PasswordInput
            name="password"
            label={t('settings.changePassword.newPassword.label')}
            placeholder={t('settings.changePassword.newPassword.placeholder')}
          />
          <Input
            name="passwordRepeat"
            label={t('settings.changePassword.newPasswordRepeat.label')}
            placeholder={t(
              'settings.changePassword.newPasswordRepeat.placeholder',
            )}
            type="password"
          />
          <SubmitButton color="accent">
            <Trans i18nKey="settings.changePassword.submit" />
          </SubmitButton>
        </Form>
      </Formik>
    </SettingContent>
  );
};
